import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSEO } from "@/hooks/use-seo";
import { CheckCircle2, Scale, TrendingUp, Baby, AlertTriangle, FileText, BarChart3 } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/use-subscription";
import { toast } from "sonner";

const Collectors: React.FC = () => {
  usePageSEO(
    "Zoocertica for Reptile, Amphibian, and Aquatic Collectors",
    "Professional collection management tools for responsible private keepers. Track inventory, feeding, breeding, and create transfer certificates.",
    "/collectors"
  );

  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { createCheckout } = useSubscription();
  const [isProcessing, setIsProcessing] = React.useState(false);

  const features = [
    {
      icon: Scale,
      title: "Inventory Management",
      description: "Complete animal records with photos, genetics, and acquisition history"
    },
    {
      icon: TrendingUp,
      title: "Growth & Feeding Tracking",
      description: "Log feeding schedules, growth measurements, and dietary changes"
    },
    {
      icon: Baby,
      title: "Breeding Management",
      description: "Track breeding pairs, clutches, and offspring lineage"
    },
    {
      icon: AlertTriangle,
      title: "Legality Alerts",
      description: "Stay informed about species regulations and permit requirements"
    },
    {
      icon: FileText,
      title: "Transfer Certificates",
      description: "Generate official transfer documentation for sales and trades"
    },
    {
      icon: BarChart3,
      title: "Reports & Analytics",
      description: "Export data for compliance, insurance, or personal records"
    }
  ];

  const pricingPlans = [
    {
      name: "Collector Starter",
      code: "collector_starter",
      monthlyPrice: 20,
      yearlyPrice: 200,
      limits: "Up to 20 animals",
      entries: "200 entries/month",
      isPopular: false
    },
    {
      name: "Collector Plus",
      code: "collector_plus",
      monthlyPrice: 50,
      yearlyPrice: 500,
      limits: "Up to 200 animals",
      entries: "Unlimited entries",
      features: "Advanced genetics tracking",
      isPopular: true
    },
    {
      name: "Collector Pro",
      code: "collector_pro",
      monthlyPrice: 99,
      yearlyPrice: 990,
      limits: "Unlimited animals",
      entries: "Unlimited entries",
      features: "Advanced genetics + Priority support",
      isPopular: false
    }
  ];

  const handleCheckout = async (planCode: string, billingCycle: "monthly" | "yearly") => {
    if (!isAuthenticated) {
      toast.error("Please sign in to subscribe");
      navigate("/auth?tab=signup");
      return;
    }

    setIsProcessing(true);
    try {
      createCheckout({ planCode, billingCycle });
    } catch (error: any) {
      toast.error(error.message || "Failed to start checkout");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddToZooAccount = () => {
    if (!isAuthenticated) {
      toast.error("Please sign in to add to your Zoo account");
      navigate("/auth");
      return;
    }
    navigate("/admin/billing");
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative px-4 py-16 lg:py-24">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white">
              Zoocertica for Reptile, Amphibian, and Aquatic Collectors
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/90 max-w-3xl mx-auto">
              The same platform trusted by professional facilities — simplified for responsible private keepers.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                size="lg"
                className="bg-white text-primary hover:bg-white/90 shadow-elegant"
              >
                View Pricing
              </Button>
              <Button
                onClick={() => navigate("/auth?tab=signup")}
                variant="outline"
                size="lg"
                className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm"
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Everything You Need to Manage Your Collection
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Professional-grade tools designed for serious collectors
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-elegant hover:shadow-glow transition-all duration-300">
                <CardHeader>
                  <div className="h-12 w-12 rounded-full bg-gradient-primary p-3 mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Choose Your Plan
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              14-day free trial on all plans • No credit card required to start
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.code}
                className={`relative ${
                  plan.isPopular
                    ? "border-2 border-primary shadow-glow"
                    : "border shadow-elegant"
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">${plan.monthlyPrice}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    or ${plan.yearlyPrice}/year (save ~17%)
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span>{plan.limits}</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span>{plan.entries}</span>
                    </li>
                    {plan.features && (
                      <li className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{plan.features}</span>
                      </li>
                    )}
                  </ul>
                  <div className="space-y-2">
                    <Button
                      onClick={() => handleCheckout(plan.code, "monthly")}
                      disabled={isProcessing}
                      className="w-full"
                      variant={plan.isPopular ? "default" : "outline"}
                    >
                      Start Free Trial
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zoo Add-On Section */}
      <section className="py-20 bg-background">
        <div className="mx-auto max-w-4xl px-4 md:px-6 lg:px-8">
          <Card className="border-2 border-primary/50 shadow-glow">
            <CardHeader>
              <CardTitle className="text-2xl sm:text-3xl text-center">
                Already a Zoo or Sanctuary User?
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-lg text-muted-foreground mb-6">
                Add Collector features to your existing account at <span className="font-bold text-primary">50% off</span> your chosen tier.
                Instantly enable reptile and aquatic collection management within your facility dashboard.
              </p>
              <Button
                onClick={handleAddToZooAccount}
                size="lg"
                className="bg-gradient-primary text-white hover:opacity-90 shadow-elegant"
              >
                Add to Zoo Account
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Collectors;
